import { useState } from 'react';
import { ArrowLeftRight, Eye, EyeOff, Maximize2, X } from 'lucide-react';
import { ThemePreviewEngine } from './ThemePreviewEngine';

interface ColorScale {
  '50': string;
  '100': string;
  '200': string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  '700': string;
  '800': string;
  '900': string;
  '950': string;
}

interface ThemeConfig {
  primaryColors: ColorScale;
  accentColors: ColorScale;
  surfaceColors: ColorScale;
  layoutType: string;
  buttonVariant: string;
  cardVariant: string;
  tableVariant: string;
  fontFamily: string;
  borderRadius: string;
}

interface ThemeComparisonProps {
  currentTheme: ThemeConfig;
  newTheme: ThemeConfig;
  onClose?: () => void;
}

export function ThemeComparison({ currentTheme, newTheme, onClose }: ThemeComparisonProps) {
  const [viewMode, setViewMode] = useState<'split' | 'slider' | 'toggle'>('split');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showCurrent, setShowCurrent] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const containerClass = isFullscreen 
    ? 'fixed inset-0 z-50 bg-surface-950 p-6' 
    : 'bg-surface-900 rounded-xl border border-surface-700 p-4';

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-primary-400" />
            Comparar Temas
          </h3>
          
          {/* View Mode Selector */}
          <div className="flex items-center gap-1 bg-surface-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('split')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                viewMode === 'split' 
                  ? 'bg-primary-600 text-white' 
                  : 'text-surface-400 hover:text-white'
              }`}
            >
              Lado a Lado
            </button>
            <button
              onClick={() => setViewMode('slider')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                viewMode === 'slider' 
                  ? 'bg-primary-600 text-white' 
                  : 'text-surface-400 hover:text-white'
              }`}
            >
              Slider
            </button>
            <button
              onClick={() => setViewMode('toggle')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                viewMode === 'toggle' 
                  ? 'bg-primary-600 text-white' 
                  : 'text-surface-400 hover:text-white'
              }`}
            >
              Alternar
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Comparison View */}
      <div className="relative">
        {viewMode === 'split' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-surface-400 mb-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-surface-500" />
                Tema Atual
              </div>
              <ThemePreviewEngine theme={currentTheme as any} mode="dark" />
            </div>
            <div>
              <div className="text-sm text-surface-400 mb-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary-500" />
                Novo Tema
              </div>
              <ThemePreviewEngine theme={newTheme as any} mode="dark" />
            </div>
          </div>
        )}

        {viewMode === 'slider' && (
          <div className="relative overflow-hidden rounded-lg">
            {/* Current Theme (Background) */}
            <div className="w-full">
              <ThemePreviewEngine theme={currentTheme as any} mode="dark" />
            </div>
            
            {/* New Theme (Overlay with clip) */}
            <div 
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <ThemePreviewEngine theme={newTheme as any} mode="dark" />
            </div>

            {/* Slider Handle */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
              style={{ left: `${sliderPosition}%` }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
                <ArrowLeftRight className="w-4 h-4 text-surface-900" />
              </div>
            </div>

            {/* Slider Input (invisible, for interaction) */}
            <input
              type="range"
              min="0"
              max="100"
              value={sliderPosition}
              onChange={(e) => setSliderPosition(Number(e.target.value))}
              className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
            />

            {/* Labels */}
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white">
              Atual
            </div>
            <div className="absolute bottom-2 right-2 px-2 py-1 bg-primary-500/80 rounded text-xs text-white">
              Novo
            </div>
          </div>
        )}

        {viewMode === 'toggle' && (
          <div>
            <div className="flex items-center justify-center gap-4 mb-4">
              <button
                onClick={() => setShowCurrent(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showCurrent 
                    ? 'bg-surface-700 text-white' 
                    : 'text-surface-400 hover:text-white'
                }`}
              >
                <div className="w-2 h-2 rounded-full bg-surface-500" />
                Tema Atual
              </button>
              <button
                onClick={() => setShowCurrent(false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  !showCurrent 
                    ? 'bg-primary-600 text-white' 
                    : 'text-surface-400 hover:text-white'
                }`}
              >
                <div className="w-2 h-2 rounded-full bg-primary-500" />
                Novo Tema
              </button>
            </div>
            <div className="transition-all duration-300">
              <ThemePreviewEngine 
                theme={showCurrent ? currentTheme as any : newTheme as any} 
                mode="dark" 
              />
            </div>
          </div>
        )}
      </div>

      {/* Changes Summary */}
      <div className="mt-4 p-3 bg-surface-800 rounded-lg">
        <h4 className="text-sm font-medium text-white mb-2">Alterações Detectadas</h4>
        <div className="flex flex-wrap gap-2">
          {currentTheme.layoutType !== newTheme.layoutType && (
            <span className="px-2 py-1 bg-primary-500/20 text-primary-400 rounded text-xs">
              Layout: {currentTheme.layoutType} → {newTheme.layoutType}
            </span>
          )}
          {currentTheme.buttonVariant !== newTheme.buttonVariant && (
            <span className="px-2 py-1 bg-primary-500/20 text-primary-400 rounded text-xs">
              Botões: {currentTheme.buttonVariant} → {newTheme.buttonVariant}
            </span>
          )}
          {currentTheme.cardVariant !== newTheme.cardVariant && (
            <span className="px-2 py-1 bg-primary-500/20 text-primary-400 rounded text-xs">
              Cards: {currentTheme.cardVariant} → {newTheme.cardVariant}
            </span>
          )}
          {currentTheme.primaryColors['500'] !== newTheme.primaryColors['500'] && (
            <span className="px-2 py-1 bg-primary-500/20 text-primary-400 rounded text-xs flex items-center gap-1">
              Cor Primária:
              <span 
                className="w-3 h-3 rounded-full inline-block"
                style={{ backgroundColor: `rgb(${currentTheme.primaryColors['500']})` }}
              />
              →
              <span 
                className="w-3 h-3 rounded-full inline-block"
                style={{ backgroundColor: `rgb(${newTheme.primaryColors['500']})` }}
              />
            </span>
          )}
          {currentTheme.layoutType === newTheme.layoutType && 
           currentTheme.buttonVariant === newTheme.buttonVariant &&
           currentTheme.cardVariant === newTheme.cardVariant &&
           currentTheme.primaryColors['500'] === newTheme.primaryColors['500'] && (
            <span className="text-surface-400 text-xs">Nenhuma alteração detectada</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default ThemeComparison;
